"use client";

import { useLocale } from "next-intl";
import { Eye, Target, Mountain } from "lucide-react";
import arMessages from "../../messages/ar.json";
import enMessages from "../../messages/en.json";

export function BrandingPanel() {
  const locale = useLocale();
  const b = locale === "ar" ? arMessages.branding : enMessages.branding;
  const panelDir = locale === "ar" ? "rtl" : "ltr";

  return (
    <aside
      dir={panelDir}
      className="relative flex flex-col items-center justify-center min-h-screen px-16 py-10 overflow-hidden text-white"
      style={{
        // FFAA brand color was not explicitly published on ffaa-fuj.com.
        // Keeping the dark maroon gradient already used in the SSO visual system
        // so the panel stays consistent with the rest of the platform until an
        // official FFAA palette is provided.
        backgroundImage:
          "linear-gradient(90deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.1) 100%), linear-gradient(-35deg, #2a0008 28%, #5a0014 90%)",
      }}
    >
      {/* FFAA medallion (official) */}
      <div className="flex-1 flex items-center justify-center w-full max-w-[480px] mb-4">
        <img
          src="/ffaa-mark.png"
          alt={b.logoAlt}
          className="h-[220px] w-auto object-contain drop-shadow-2xl"
        />
      </div>

      {/* FFAA wordmark (official) */}
      <img
        src="/ffaa-wordmark.png"
        alt={b.logoAltHorizontal}
        className="h-16 w-auto object-contain mb-3"
      />

      {/* System Title */}
      <h1 className="text-[18px] font-medium text-center text-white/90 mb-8 leading-relaxed">
        {b.systemTitle}
      </h1>

      {/* Info Cards */}
      <div className="w-full space-y-1.5">
        {/* Vision Card */}
        <div className="rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="shrink-0 text-white/80 mt-0.5">
              <Eye className="h-6 w-6" />
            </div>
            <div className="flex-1 text-start">
              <h3 className="text-sm font-bold text-white mb-1">{b.vision}</h3>
              <p className="text-xs leading-relaxed text-white/90">
                {b.visionText}
              </p>
            </div>
          </div>
        </div>

        {/* Mission Card */}
        <div className="rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="shrink-0 text-white/80 mt-0.5">
              <Target className="h-6 w-6" />
            </div>
            <div className="flex-1 text-start">
              <h3 className="text-sm font-bold text-white mb-1">{b.mission}</h3>
              <p className="text-xs leading-relaxed text-white/90">
                {b.missionText}
              </p>
            </div>
          </div>
        </div>

        {/* Strategic Goals Card */}
        <div className="rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="shrink-0 text-white/80 mt-0.5">
              <Mountain className="h-6 w-6" />
            </div>
            <div className="flex-1 text-start">
              <h3 className="text-sm font-bold text-white mb-2">{b.strategicGoals}</h3>
              {/* Strategic Goals Grid */}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-white/90 leading-snug">
                <ul className="list-disc list-inside space-y-1.5 marker:text-white/60">
                  <li>{b.goal1}</li>
                  <li>{b.goal2}</li>
                  <li>{b.goal3}</li>
                </ul>
                <ul className="list-disc list-inside space-y-1.5 marker:text-white/60">
                  <li>{b.goal4}</li>
                  <li>{b.goal5}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
